document.addEventListener("DOMContentLoaded", () => {
  const url = new URL(window.location.href);
  const isPreviewTheme = url.searchParams.has("preview_theme_id");
  const localeInUrl = url.searchParams.get("locale");
  const htmlLocale = (document.documentElement.lang || "").toLowerCase();

  if (isPreviewTheme && !localeInUrl && htmlLocale !== "es") {
    url.searchParams.set("locale", "es");
    window.location.replace(url.toString());
    return;
  }

  const body = document.body;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const q = (selector, scope = document) => scope.querySelector(selector);
  const qAll = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const header = q("[data-vc9-header]");
  const backdrop = q("[data-vc9-backdrop]");
  const menuButton = q("[data-vc9-menu-toggle]");
  const menuDrawer = q("[data-vc9-menu]");
  const cartButton = q("[data-vc9-cart-toggle]");
  const cartDrawer = q("[data-vc9-cart]");
  const localeSelects = qAll("[data-vc9-locale-select]");

  const heroStage = q("[data-vc9-hero-stage]");
  const heroVisual = q("[data-vc9-hero-visual]");
  const heroLabel = q("[data-vc9-hero-label]");
  const heroOptions = qAll("[data-vc9-hero-option]");
  const heroImages = qAll("[data-vc9-hero-image]");

  const colorwaysStage = q("[data-vc9-colorways]");
  const colorwaysVisual = q(".vc9-colorways-stage__visual");
  const colorLabel = q("[data-vc9-color-label]");
  const colorOptions = qAll("[data-vc9-color-option]");
  const colorImages = qAll("[data-vc9-color-image]");

  const productGallery = q("[data-vc-product-gallery]");
  const productMainImage = q("[data-vc-product-image]");
  const productZoomTrigger = q("[data-vc-zoom-trigger]");
  const variantButtons = qAll("[data-vc-variant-button]");
  const variantInput = q("[data-vc-variant-input]");
  const priceNode = q("[data-vc-price]");
  const statusNode = q("[data-vc-status]");
  const primaryAction = q("[data-vc-primary-button]");
  const stickyBar = q("[data-vc-sticky-bar]");
  const stickyVariant = q("[data-vc-sticky-variant]");
  const stickyPrice = q("[data-vc-sticky-price]");
  const stickyAction = q("[data-vc-sticky-action]");
  const purchaseAnchor = q("#v9-purchase");
  const lightbox = q("[data-vc-lightbox]");
  const lightboxImage = q("[data-vc-lightbox-image]");
  const lightboxClose = q("[data-vc-close-lightbox]");

  let activeDrawer = null;
  let lastTrigger = null;
  let lastScrollY = window.scrollY;
  let layoutFrame = 0;
  let heroCycle = 0;
  let heroIndex = 0;

  const scheduleLayoutUpdate = () => {
    if (layoutFrame) return;
    layoutFrame = window.requestAnimationFrame(() => {
      layoutFrame = 0;
      updateHeaderState();
      updateParallax();
    });
  };

  const getFocusable = (node) =>
    qAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      node
    );

  const setBodyLock = (locked) => {
    body.classList.toggle("vc9-lock", locked);
  };

  const syncDrawer = (drawer, open, trigger) => {
    if (!drawer) return;

    drawer.hidden = !open;
    drawer.classList.toggle("is-open", open);
    drawer.setAttribute("aria-hidden", String(!open));

    if (drawer === menuDrawer && menuButton) {
      menuButton.setAttribute("aria-expanded", String(open));
    }

    if (drawer === cartDrawer && cartButton) {
      cartButton.setAttribute("aria-expanded", String(open));
    }

    if (backdrop) {
      backdrop.hidden = !open;
    }

    setBodyLock(open);
    activeDrawer = open ? drawer : null;

    if (open) {
      lastTrigger = trigger || null;
      const focusable = getFocusable(drawer);
      if (focusable.length) focusable[0].focus();
    } else if (lastTrigger) {
      lastTrigger.focus();
    }
  };

  const closeActiveDrawer = () => {
    if (!activeDrawer) return;
    const drawer = activeDrawer;
    activeDrawer = null;
    syncDrawer(drawer, false);
  };

  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) {
      syncDrawer(activeDrawer, false);
    }
    syncDrawer(drawer, true, trigger);
  };

  menuButton?.addEventListener("click", () => {
    if (menuDrawer?.hidden) openDrawer(menuDrawer, menuButton);
    else closeActiveDrawer();
  });

  cartButton?.addEventListener("click", () => {
    if (cartDrawer?.hidden) openDrawer(cartDrawer, cartButton);
    else closeActiveDrawer();
  });

  qAll("[data-vc9-close-menu]").forEach((button) => {
    button.addEventListener("click", () => syncDrawer(menuDrawer, false));
  });

  qAll("[data-vc9-close-cart]").forEach((button) => {
    button.addEventListener("click", () => syncDrawer(cartDrawer, false));
  });

  backdrop?.addEventListener("click", closeActiveDrawer);

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && activeDrawer) {
      syncDrawer(activeDrawer, false);
      return;
    }

    if (!activeDrawer || event.key !== "Tab") return;
    const focusable = getFocusable(activeDrawer);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const updateHeaderState = (pointerY = null) => {
    if (!header) return;

    if (window.innerWidth < 900) {
      header.classList.remove("is-hidden");
      header.classList.add("is-peek");
      lastScrollY = window.scrollY;
      return;
    }

    const currentScrollY = window.scrollY;
    const nearTop = currentScrollY < 24;
    const pointerNearTop = typeof pointerY === "number" && pointerY < 96;
    const scrollingUp = currentScrollY < lastScrollY;
    const hoveringHeader = header.matches(":hover") || header.contains(document.activeElement);
    const shouldShow = Boolean(activeDrawer) || nearTop || pointerNearTop || scrollingUp || hoveringHeader;

    header.classList.toggle("is-hidden", !shouldShow);
    header.classList.toggle("is-peek", shouldShow);
    lastScrollY = currentScrollY;
  };

  const stopHeroCycle = () => {
    if (heroCycle) {
      window.clearInterval(heroCycle);
      heroCycle = 0;
    }
  };

  const setActiveHero = (option) => {
    if (!option || !heroOptions.length) return;

    heroOptions.forEach((item) => {
      const isActive = item === option;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-selected", String(isActive));
    });

    heroImages.forEach((image) => {
      image.classList.toggle("is-active", image.dataset.imageKey === option.dataset.imageKey);
    });

    if (heroLabel) {
      heroLabel.textContent = option.dataset.name || heroLabel.textContent;
    }

    if (heroVisual && option.dataset.accent) {
      heroVisual.style.setProperty("--vc9-hero-accent", option.dataset.accent);
    }

    heroIndex = Math.max(0, heroOptions.indexOf(option));
  };

  const startHeroCycle = () => {
    if (prefersReducedMotion.matches || heroOptions.length < 2) return;
    stopHeroCycle();
    heroCycle = window.setInterval(() => {
      heroIndex = (heroIndex + 1) % heroOptions.length;
      setActiveHero(heroOptions[heroIndex]);
    }, 4200);
  };

  const setActiveColorway = (option) => {
    if (!option || !colorOptions.length) return;
    const activeIndex = Number(option.dataset.index);

    colorOptions.forEach((item) => {
      const isActive = item === option;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-pressed", String(isActive));
    });

    colorImages.forEach((image, index) => {
      image.classList.toggle("is-active", index === activeIndex);
    });

    if (colorLabel) {
      colorLabel.textContent = option.dataset.name || colorLabel.textContent;
    }
  };

  const updateParallax = () => {
    if (prefersReducedMotion.matches) {
      [heroVisual, colorwaysVisual].forEach((node) => {
        if (node) node.style.setProperty("--vc9-parallax-y", "0px");
      });
      return;
    }

    const targets = [
      { node: heroVisual, strength: 24 },
      { node: colorwaysVisual, strength: 18 },
    ].filter((item) => item.node);

    if (!targets.length) return;

    const viewportCenter = window.innerHeight / 2 || 1;
    targets.forEach(({ node, strength }) => {
      const rect = node.getBoundingClientRect();
      const distance = ((rect.top + rect.height / 2) - viewportCenter) / Math.max(window.innerHeight, 1);
      const offset = Math.max(-1, Math.min(1, distance)) * -strength;
      node.style.setProperty("--vc9-parallax-y", `${offset.toFixed(2)}px`);
    });
  };

  const setActiveVariant = (button) => {
    if (!button) return;

    const variantId = button.dataset.variantId || "";
    const variantTitle = button.dataset.variantTitle || "";
    const variantUrl = button.dataset.variantUrl || "";
    const variantImage = button.dataset.image || "";
    const variantPrice = button.dataset.priceFormatted || "";
    const variantAvailable = button.dataset.available === "true";
    const labelAvailable = "Añadir a la bolsa";
    const labelUnavailable = "Recibir aviso";

    variantButtons.forEach((item) => {
      const isActive = item === button;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-pressed", String(isActive));
    });

    if (variantInput && variantId) {
      variantInput.value = variantId;
    }

    if (productMainImage && variantImage) {
      const baseTitle = (productMainImage.alt || "MIRASAI").split(" - ")[0];
      productMainImage.src = variantImage;
      productMainImage.alt = variantTitle ? `${baseTitle} - ${variantTitle}` : baseTitle;
      if (lightboxImage) {
        lightboxImage.src = variantImage;
        lightboxImage.alt = productMainImage.alt;
      }
    }

    if (priceNode && variantPrice) {
      priceNode.textContent = variantPrice;
      priceNode.classList.toggle("vc9-price--pending", !variantAvailable);
    }

    if (statusNode) {
      statusNode.textContent = variantAvailable ? "Disponible" : "Agotado";
    }

    if (stickyVariant && variantTitle) {
      stickyVariant.textContent = variantTitle;
    }

    if (stickyPrice && variantPrice) {
      stickyPrice.textContent = variantPrice;
    }

    if (primaryAction) {
      primaryAction.textContent = variantAvailable ? labelAvailable : labelUnavailable;
      if (primaryAction.tagName === "BUTTON") {
        primaryAction.disabled = !variantAvailable;
      } else {
        primaryAction.href = variantAvailable ? "#v9-purchase" : "/pages/contact";
      }
    }

    if (stickyAction) {
      stickyAction.textContent = variantAvailable ? labelAvailable : labelUnavailable;
      stickyAction.href = variantAvailable ? "#v9-purchase" : "/pages/contact";
    }

    if (variantUrl && window.history && window.history.replaceState && !variantUrl.includes("preview_theme_id")) {
      // Preserve the preview frame rather than rewriting it away. The product state
      // is still fully reflected in the UI, so the URL update is intentionally skipped.
    }
  };

  const openLightbox = (src, alt) => {
    if (!lightbox || !lightboxImage || !src) return;
    lightboxImage.src = src;
    lightboxImage.alt = alt || "";

    if (typeof lightbox.showModal === "function") {
      lightbox.hidden = false;
      lightbox.classList.remove("is-open");
      lightbox.showModal();
      return;
    }

    lightbox.hidden = false;
    lightbox.classList.add("is-open");
    setBodyLock(true);
  };

  const closeLightbox = () => {
    if (!lightbox) return;

    if (typeof lightbox.close === "function" && lightbox.open) {
      lightbox.close();
    }

    lightbox.classList.remove("is-open");
    lightbox.hidden = true;
    setBodyLock(Boolean(activeDrawer));
  };

  updateHeaderState();
  updateParallax();

  window.addEventListener("scroll", scheduleLayoutUpdate, { passive: true });
  window.addEventListener("resize", scheduleLayoutUpdate);
  window.addEventListener("focus", scheduleLayoutUpdate);
  window.addEventListener("mousemove", (event) => updateHeaderState(event.clientY));
  window.addEventListener("pointermove", (event) => updateHeaderState(event.clientY));
  window.addEventListener("mouseleave", () => updateHeaderState());

  if (heroOptions.length) {
    heroOptions.forEach((option) => {
      option.addEventListener("click", () => {
        setActiveHero(option);
        stopHeroCycle();
        startHeroCycle();
      });
    });

    setActiveHero(heroOptions[0]);
    startHeroCycle();
    heroStage?.addEventListener("pointerenter", stopHeroCycle);
    heroStage?.addEventListener("pointerleave", startHeroCycle);
    heroVisual?.addEventListener("pointerenter", stopHeroCycle);
    heroVisual?.addEventListener("pointerleave", startHeroCycle);
  }

  if (colorOptions.length) {
    colorOptions.forEach((option) => {
      option.addEventListener("click", () => setActiveColorway(option));
    });

    setActiveColorway(colorOptions[0]);
  }

  localeSelects.forEach((select) => {
    select.addEventListener("change", () => {
      const form = select.closest("form");
      if (form && typeof form.requestSubmit === "function") {
        form.requestSubmit();
        return;
      }

      if (!select.value) return;
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set("locale", select.value);
      window.location.href = nextUrl.toString();
    });
  });

  if (variantButtons.length) {
    variantButtons.forEach((button) => {
      button.addEventListener("click", () => setActiveVariant(button));
    });

    setActiveVariant(
      variantButtons.find((button) => button.classList.contains("is-active")) || variantButtons[0]
    );
  }

  if (productZoomTrigger && productMainImage) {
    productZoomTrigger.addEventListener("click", () => {
      openLightbox(productMainImage.src, productMainImage.alt);
    });
  }

  lightbox?.addEventListener("click", (event) => {
    if (event.target === lightbox) closeLightbox();
  });

  lightboxClose?.addEventListener("click", closeLightbox);

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && lightbox && lightbox.classList.contains("is-open")) {
      closeLightbox();
    }
  });

  if (stickyBar && purchaseAnchor && "IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.hidden = entry.isIntersecting;
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -35% 0px",
      }
    );
    observer.observe(purchaseAnchor);
  }
});
